/**
 * Lexicon service — S4 (VE-24948 / U5).
 *
 * All aiWARE structured-data reads and writes for lexicons. Three properties
 * are load-bearing and easy to lose in a refactor:
 *
 * 1. EVERY WRITE IS READ-MODIFY-WRITE UNDER A REVISION GUARD (BR-6).
 *    `updateStructuredData` replaces `data` wholesale — there is no patch
 *    operation — and the flat permission model (U5-FR3) makes two admins
 *    editing one lexicon routine rather than exotic. Without the guard, a
 *    concurrent CSV import and a manual edit silently lose one another's work,
 *    which would violate U5-FR13 ("a further CSV ADDS terms") by the system's
 *    own doing. Last-write-wins and blind merge are both forbidden.
 *
 * 2. PERMISSIONS ARE NOT ENFORCED HERE (SEC-R5).
 *    SDO mutations are gated server-side by `@requireAuthRole(permissions:
 *    [AIWARE_SDO_CREATE], orgRole: [...])` and reads by `AIWARE_SDO_READ`. Flat
 *    permissions are expressed by GRANTING org users those SDO permissions, so
 *    enforcement is structural. This module must not add a client-side check —
 *    SEC-R5 forbids enforcing by hiding UI, and the corollary is that the
 *    client must not be trusted for it either.
 *
 * 3. THIS IS WHERE A REGEX ENTRY IS ACTUALLY GATED (ADR-001 as amended).
 *    `assertNewRegexEntriesScreened` runs the syntactic screen AND the measured
 *    probe on every regex entry a write adds or changes, and refuses the write
 *    if either fails — including when the probe cannot run. Three earlier
 *    designs inferred a pattern's cost from its shape and were each bypassed by
 *    measurement, so the decision moved here, to the one place every write path
 *    passes through: the drawer, the CSV importer, a migration, a script. The
 *    drawer probes too, but as an affordance; this is the authority.
 *
 * Org scoping likewise comes from the platform: `structuredDataObjects` returns
 * only the caller's org's objects.
 */

import { v4 as uuid } from 'uuid';

import { GraphQLExecutor } from './executor';
import {
  COUNT_DELETED_LEXICONS,
  CountDeletedLexiconsResponse,
  CREATE_LEXICON,
  CreateLexiconResponse,
  DELETE_LEXICON,
  DeleteLexiconResponse,
  FETCH_LEXICON_BY_ID,
  FETCH_LEXICONS,
  FETCH_LEXICONS_BY_NAME_KEY,
  lexiconNameKeyFilter,
  FetchLexiconsResponse,
  LexiconSdoRecord,
  CREATE_LEXICON_ENTRIES,
  LEXICON_DELETED_FILTER,
  LEXICON_SUMMARY_FILTER,
  UPDATE_LEXICON_ENTRIES,
  operationNameOf,
  UPDATE_LEXICON,
  UpdateLexiconResponse,
} from './queries';
import { withLexiconSchemaId } from './schemaResolver';
import {
  Lexicon,
  LexiconData,
  LexiconEntriesData,
  LexiconEntriesRecord,
  LexiconEntry,
  LexiconEntryInput,
  LexiconSummary,
  LexiconSummaryData,
  lexiconNameKey,
  deletedEntries,
  liveEntries,
} from '../models';
import { ENTRY_REGEX_MAX_LENGTH, LEXICON_MAX_ENTRIES } from '../limits';
import { failRegexScreen, validateRegexEntry } from '../regexScreen';
import { probeRegexPerformance } from '../regexProbeClient';

/**
 * Raised when a write lost a revision race (BR-6).
 *
 * Carries the other actor's identity because "someone else changed this" is not
 * actionable, while "Dana changed this two minutes ago" is.
 */
export class LexiconRevisionConflictError extends Error {
  constructor(
    public readonly lexiconId: string,
    public readonly expectedRevision: number,
    public readonly actualRevision: number,
    public readonly modifiedBy: string,
    public readonly modifiedDateTime: string
  ) {
    super(
      `Lexicon ${lexiconId} was modified by ${modifiedBy} ` +
        `(revision ${actualRevision}, expected ${expectedRevision}).`
    );
    this.name = 'LexiconRevisionConflictError';
  }
}

export class LexiconNotFoundError extends Error {
  constructor(lexiconId: string) {
    super(`Lexicon ${lexiconId} was not found.`);
    this.name = 'LexiconNotFoundError';
  }
}

/** Runtime-config slice this service needs. Injected, never read from a global. */
export interface LexiconServiceConfig {
  /**
   * The DATA REGISTRY id — never a schema id. See `schemaResolver.ts` for why
   * pinning a schema id is forbidden (VE-24546, VE-25957).
   */
  readonly lexiconRegistryId?: string;
  /**
   * The regex performance probe, injectable for tests.
   *
   * Production leaves it unset and gets `probeRegexPerformance`, which spawns a
   * real worker. A unit test cannot: jsdom provides a `Worker` constructor that
   * never delivers a message, so the default would sit out the full
   * `REGEX_PROBE_DEADLINE_MS` and then — correctly — fail closed, refusing
   * every regex write in the suite. Injecting is what lets a test assert the
   * gate's behaviour, including the fail-closed paths, in milliseconds.
   *
   * It is NOT a way to disable the gate in production. Nothing in `src/` passes
   * it, and the sole default is the real probe.
   */
  readonly probeRegex?: typeof probeRegexPerformance;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asEntry(value: unknown): LexiconEntry | null {
  if (!isRecord(value)) {
    return null;
  }
  const { entryId, term, type } = value;
  if (
    typeof entryId !== 'string' ||
    typeof term !== 'string' ||
    (type !== 'term' && type !== 'regex')
  ) {
    return null;
  }
  /**
   * SCREEN A REGEX ON THE WAY IN, not only on the way out.
   *
   * The write paths screen and probe, but this function is how every entry
   * re-enters the app, and it used to check only that `term` was a string —
   * so a pattern written straight to the registry (the id is a constant, the
   * schema id is client-resolvable, and the schema caps `term` only at
   * `maxLength: 500`) reached `new RegExp` in the match worker unscreened.
   *
   * This is the SYNTACTIC screen only. The measured probe needs a worker and
   * cannot run here, so the boundary is honestly: syntactic on read,
   * syntactic plus measured on write. That still rejects invalid syntax and
   * every rule certain from the characters alone (length, backreference,
   * lookaround, repeat count, quantifier count), which is what a
   * hand-written pattern is most likely to trip.
   *
   * Dropped rather than kept, consistent with how this function treats every
   * other untrusted field. The drop is not silent to the app: the entry
   * simply is not in the lexicon, so `entryCount` and the drawer disagree
   * with the registry, which is the visible symptom a reader can act on.
   */
  if (type === 'regex' && !validateRegexEntry(term).ok) {
    return null;
  }
  return {
    entryId,
    term,
    type,
    ...(typeof value.caseSensitive === 'boolean'
      ? { caseSensitive: value.caseSensitive }
      : {}),
    ...(typeof value.wholeWord === 'boolean'
      ? { wholeWord: value.wholeWord }
      : {}),
    ...(typeof value.redactionCodeId === 'string'
      ? { redactionCodeId: value.redactionCodeId }
      : {}),
    // `kind` is deliberately NOT read. Keyword and Phrase were merged into one
    // kind, which is now a reading of `type`, so a `kind` on the wire is an
    // older client's label with nothing left to say. Dropping it here is what
    // makes that need no migration: the value stays in the registry, unread,
    // until the record is next written.
    // Load-bearing, not cosmetic: drop this and every tombstone comes back
    // from the registry as a LIVE entry, so a term the agency deleted starts
    // redacting documents again on the next read.
    ...(value.isDeleted === true ? { isDeleted: true } : {}),
  };
}

/**
 * Converts one SDO record into a `Lexicon`, dropping anything malformed.
 *
 * Deliberately tolerant rather than throwing: an SDO written by an older client,
 * or hand-edited in the platform UI, must not make the whole settings page
 * unopenable. A lexicon with unreadable entries is better than no page.
 *
 * `revision` defaults to 0 when absent so a pre-guard record still participates
 * in the guard — its first write moves it to 1 and it behaves normally after.
 */
/**
 * Parses one SUMMARY record. `null` for anything that is not one.
 *
 * The `null` is a backstop as well as a parse failure: a detail record has no
 * `name`, so even if the list's `recordPart` filter were somehow not applied,
 * details would not surface as lexicons. The visible symptom would be an
 * inflated `count` and short pages — which `recordCount` already exists for —
 * rather than wrong data.
 */
export function lexiconSummaryFromSdo(
  record: LexiconSdoRecord
): LexiconSummary | null {
  if (!isRecord(record.data)) {
    return null;
  }
  const data = record.data;
  if (typeof data.name !== 'string' || !data.name) {
    return null;
  }
  return {
    id: record.id,
    name: data.name,
    revision: typeof data.revision === 'number' ? data.revision : 0,
    entriesId: typeof data.entriesId === 'string' ? data.entriesId : '',
    entryCount: typeof data.entryCount === 'number' ? data.entryCount : 0,
    deletedEntryCount:
      typeof data.deletedEntryCount === 'number' ? data.deletedEntryCount : 0,
    createdDateTime:
      typeof data.createdDateTime === 'string'
        ? data.createdDateTime
        : record.createdDateTime,
    modifiedDateTime:
      typeof data.modifiedDateTime === 'string'
        ? data.modifiedDateTime
        : record.modifiedDateTime,
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    modifiedBy: typeof data.modifiedBy === 'string' ? data.modifiedBy : '',
    // Defaulted, unlike the three below, because it is REQUIRED on the record
    // and the type says so. The default is defensive only: a record reaching
    // here without it could not have come back from either filtered list
    // query, so in practice this arm is unreachable — but a parser that threw,
    // or returned `undefined` into a `boolean`, would be worse than one that
    // reads a hand-edited record as live.
    isDeleted: data.isDeleted === true,
    // Housekeeping fields are OMITTED when absent rather than defaulted, so a
    // record that has never carried one stays a record that has never carried
    // one — `undefined` and `false` mean different things, and the difference
    // is the only evidence of which records predate the field. Read them back
    // out; write them back unchanged.
    ...(typeof data.displayOrder === 'number'
      ? { displayOrder: data.displayOrder }
      : {}),
    ...(typeof data.enabled === 'boolean' ? { enabled: data.enabled } : {}),
    ...(typeof data.autoApply === 'boolean'
      ? { autoApply: data.autoApply }
      : {}),
  };
}

/** Parses one DETAIL record. Tolerant of malformed entries, like the old path. */
export function lexiconEntriesFromSdo(
  record: LexiconSdoRecord
): LexiconEntriesRecord | null {
  if (!isRecord(record.data)) {
    return null;
  }
  const data = record.data;
  const rawEntries = Array.isArray(data.entries) ? data.entries : [];
  return {
    id: record.id,
    lexiconId: typeof data.lexiconId === 'string' ? data.lexiconId : '',
    revision: typeof data.revision === 'number' ? data.revision : 0,
    entries: rawEntries
      .map(asEntry)
      .filter((e): e is LexiconEntry => e !== null),
  };
}

/**
 * The ONE place `nameLower` is produced (review nit N14).
 *
 * Every summary write goes through here, so the folded name cannot be written
 * without its name or drift from it — and because it is not on `LexiconSummary`
 * itself, no caller can set it by hand and get the two out of step.
 */
function toSummaryData(summary: LexiconSummary): LexiconSummaryData {
  const { id: _id, ...data } = summary;
  return {
    recordPart: 'summary',
    ...data,
    nameLower: lexiconNameKey(data.name),
  };
}

function toEntriesData(record: LexiconEntriesRecord): LexiconEntriesData {
  const { id: _id, ...data } = record;
  // `isDeleted: false` is inert on a detail — see `LexiconEntriesData`. It is
  // stamped because the schema requires it on EVERY record, which is what keeps
  // the two filtered list queries from ever silently losing a row.
  return { recordPart: 'entries', isDeleted: false, ...data };
}

/** A whole lexicon minus its id — what the pure mutators return. */
function toData(lexicon: Lexicon): LexiconData {
  const { id: _id, ...data } = lexicon;
  return data;
}

/** The summary half of a hydrated lexicon, for writing it back. */
function summaryOf(
  lexicon: Lexicon
): Omit<LexiconSummary, 'entryCount' | 'deletedEntryCount'> {
  const { entries: _entries, ...summary } = lexicon;
  return summary;
}

export interface FetchLexiconsResult {
  /** At most `limit` of them: the probe record, if any, is not one of these. */
  readonly lexicons: readonly LexiconSummary[];
  /**
   * Whether a record exists BEYOND this page — exactly, not approximately.
   *
   * It is measured by asking for one record more than the page holds and
   * seeing whether it arrives, and it is measured on the RAW records, before
   * unparseable ones are dropped: a malformed row still occupies a place in
   * the list, and comparing `lexicons.length` with the page size instead is
   * what ended a scan early on one bad record (review-gate R8).
   *
   * THERE IS DELIBERATELY NO `count` HERE, and the query no longer asks for
   * one. `structuredDataObjects.count` is `rows.length` of the page the server
   * returned, so it is capped by `limit` and is never a total of everything
   * matching the filter. Carrying it invited exactly one reading, and every
   * caller that took it made the same mistake: the deleted link read the
   * registry's count off a `limit: 1` query and said "(1)" forever, the
   * auto-apply scan stopped after its first full page, and the table's pager
   * disabled Next on every page (VE-29495). "Is there another page" is the
   * only question any of them was really asking, and this answers it.
   */
  readonly hasMore: boolean;
}

/**
 * Lists lexicons WITHOUT their entries.
 *
 * The whole point of the two-record split: a page of 25 summaries is 25 names
 * and 25 counts, where the single-record version transferred every entry of
 * every one of them. Nothing on the settings table, the upload selector or the
 * editor's apply panel needs more than this.
 */
export function fetchLexicons(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  page: {
    readonly offset: number;
    readonly limit: number;
    /** The Deleted Lexicons view. Two lists, one query, one flag. */
    readonly deleted?: boolean;
  }
): Promise<FetchLexiconsResult> {
  return withLexiconSchemaId(
    exec,
    config.lexiconRegistryId,
    async (schemaId) => {
      const response = await exec<FetchLexiconsResponse>({
        query: FETCH_LEXICONS,
        variables: {
          schemaId,
          offset: page.offset,
          // ONE MORE THAN THE PAGE HOLDS. The extra record is never shown; its
          // arrival IS the answer to "is there another page", which the
          // registry cannot be asked directly — it reports the size of the
          // page it returned and nothing about what follows (VE-29495).
          // Comparing a full page with the page size would guess instead, and
          // guess wrong on a list whose length is an exact multiple of it.
          limit: page.limit + 1,
          filter: page.deleted
            ? LEXICON_DELETED_FILTER
            : LEXICON_SUMMARY_FILTER,
        },
        operationName: operationNameOf(FETCH_LEXICONS),
      });
      const records = response?.structuredDataObjects?.records ?? [];
      // Trimmed BEFORE parsing, so the probe cannot be rendered as a row and
      // cannot be counted twice: it is the first record of the NEXT page.
      const hasMore = records.length > page.limit;
      const onPage = hasMore ? records.slice(0, page.limit) : records;
      return {
        lexicons: onPage
          .map(lexiconSummaryFromSdo)
          .filter((l): l is LexiconSummary => l !== null),
        hasMore,
      };
    }
  );
}

/** Reads one record of either kind, by SDO id. */
async function fetchRecordById(
  exec: GraphQLExecutor,
  schemaId: string,
  id: string
): Promise<LexiconSdoRecord | null> {
  const response = await exec<FetchLexiconsResponse>({
    query: FETCH_LEXICON_BY_ID,
    variables: { schemaId, id },
    operationName: operationNameOf(FETCH_LEXICON_BY_ID),
  });
  return response?.structuredDataObjects?.records?.[0] ?? null;
}

/** Reads one lexicon's summary. The "read" half of a rename or delete. */
export function fetchLexiconSummaryById(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  lexiconId: string
): Promise<LexiconSummary> {
  return withLexiconSchemaId(
    exec,
    config.lexiconRegistryId,
    async (schemaId) => {
      const record = await fetchRecordById(exec, schemaId, lexiconId);
      const summary = record ? lexiconSummaryFromSdo(record) : null;
      if (!summary) {
        throw new LexiconNotFoundError(lexiconId);
      }
      return summary;
    }
  );
}

/** Reads one lexicon's entries. The "read" half of every entry write. */
export function fetchLexiconEntries(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  summary: LexiconSummary
): Promise<LexiconEntriesRecord> {
  return withLexiconSchemaId(
    exec,
    config.lexiconRegistryId,
    async (schemaId) => {
      const record = summary.entriesId
        ? await fetchRecordById(exec, schemaId, summary.entriesId)
        : null;
      const entries = record ? lexiconEntriesFromSdo(record) : null;
      if (!entries) {
        throw new LexiconNotFoundError(summary.entriesId || summary.id);
      }
      return entries;
    }
  );
}

/**
 * Reads a lexicon WITH its entries — two records, assembled.
 *
 * Used by the drawer and the apply path, which are the only places that need
 * the entries. Two round trips where there was one, on a path that already
 * awaited one per lexicon.
 *
 * It also HEALS the count: if the summary's cached `entryCount` disagrees with
 * what the detail actually holds, the summary is corrected in passing. That is
 * the whole reconciliation story — no job, no sweep, just the next reader
 * fixing what the last writer could not finish. Failing to heal is ignored: a
 * stale count must never turn a successful read into an error.
 */
export async function fetchLexiconById(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  lexiconId: string
): Promise<Lexicon> {
  const summary = await fetchLexiconSummaryById(exec, config, lexiconId);
  const detail = await fetchLexiconEntries(exec, config, summary);
  const trueCount = liveEntries(detail.entries).length;
  const trueDeleted = deletedEntries(detail.entries).length;
  if (
    trueCount !== summary.entryCount ||
    trueDeleted !== summary.deletedEntryCount
  ) {
    try {
      await writeSummary(exec, config, {
        ...summary,
        entryCount: trueCount,
        deletedEntryCount: trueDeleted,
      });
    } catch {
      // See above: the read still succeeded, and the count is cosmetic.
    }
  }
  const { entryCount: _count, ...rest } = summary;
  return { ...rest, entries: detail.entries };
}

/**
 * Every LIVE lexicon summary in the org.
 *
 * AUTO-APPLY needs this, and it is now the only caller: `loadAutoApplyLexiconIds`
 * has to consider every live lexicon in the org, and "which of them is flagged"
 * is not a question a page can answer. The BR-2 name check used to be the other
 * caller and no longer is — it filters on `nameLower` in one query now (N14).
 * The settings page has never used it: that pages server-side, and the deleted
 * view is its own filtered query.
 *
 * Live only.
 *
 * ONE STOP CONDITION, plus a cap. `hasMore` means the server held a record
 * beyond the page it just served — `fetchLexicons` asks for one extra and
 * reports whether it came. It is decided on the RAW records, so a row this app
 * cannot parse does not make a full page look short and end the scan early
 * (review-gate R8).
 *
 * There used to be a second, `count`-based fast path
 * (`offset + PAGE >= count`). It is gone because `count` is page-sized, not a
 * total: after any full page it read 100, the condition held, and the scan
 * stopped at the first page — auto-apply never saw a lexicon past the 100th
 * (VE-29495 review E1). `fetchLexicons` no longer returns a `count` to be
 * tempted by.
 *
 * Returning a SHORT list is the failure mode when the cap is hit: an
 * auto-apply lexicon past the cap does not run on first open. That is the
 * right direction — a lexicon that fails to apply is visibly absent from the
 * panel, while a hung page is not diagnosable at all.
 *
 * This used to add "and the platform is the real uniqueness gate", about the
 * BR-2 check that no longer lives here. There is no such gate: an SDO registry
 * has no cross-record unique constraint and a JSON schema cannot express one
 * (review R3). Nothing downstream should be written as though one exists.
 */
export async function fetchAllLiveLexicons(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig
): Promise<readonly LexiconSummary[]> {
  const PAGE = 100;
  const all: LexiconSummary[] = [];
  for (let offset = 0; ; offset += PAGE) {
    const { lexicons, hasMore } = await fetchLexicons(exec, config, {
      offset,
      limit: PAGE,
    });
    all.push(...lexicons);
    if (!hasMore) {
      return all;
    }
    if (offset + PAGE >= PAGE * MAX_SCAN_PAGES) {
      return all;
    }
  }
}

/**
 * The most this link will name before it stops counting: "100+".
 *
 * The number is a label on a link. Nobody acts on the difference between 214
 * and 215 deleted lexicons, and nobody has that many — so the count is worth
 * ONE bounded query, never a scan whose cost grows with the trash.
 */
export const DELETED_COUNT_MAX = 100;

/** The "(N)" on the link, and whether N is the whole story. */
export interface DeletedLexiconCount {
  /** Exact when `exact`; otherwise `DELETED_COUNT_MAX`, rendered as "100+". */
  readonly count: number;
  readonly exact: boolean;
}

/**
 * How many soft-deleted lexicons the org holds — the "(N)" on the settings
 * page's "View deleted lexicons" link.
 *
 * ONE QUERY, for `DELETED_COUNT_MAX + 1` ids, counting the records that come
 * back. Asking for one more than the label can show is what makes "more than
 * 100" a fact about this response instead of a second query: 101 records back
 * means at least 101 exist, and the link reads "100+".
 *
 * WHY NOT THE REGISTRY'S OWN `count`. Because it is `rows.length` of the page
 * returned, capped by `limit` — reading it off a `limit: 1` query is what made
 * this link say "(1)" however many were deleted (VE-29495). There is no
 * count-only aggregate on the SDO API, so counting records IS the count.
 *
 * WHY NOT PAGE THE WHOLE SET for an exact number. It was written that way
 * first, and it cost one query per 100 deleted on every list fetch, to
 * distinguish numbers no user reads and no code branches on. The one branch
 * that does — the Deleted view closing itself when the trash empties — turns
 * on zero, which this answers exactly.
 */
export function countDeletedLexicons(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig
): Promise<DeletedLexiconCount> {
  return withLexiconSchemaId(
    exec,
    config.lexiconRegistryId,
    async (schemaId) => {
      const response = await exec<CountDeletedLexiconsResponse>({
        query: COUNT_DELETED_LEXICONS,
        variables: {
          schemaId,
          limit: DELETED_COUNT_MAX + 1,
          // The DELETED SUMMARIES, both halves load-bearing: `recordPart`
          // keeps the detail record that carries the entries out of the count,
          // so a deleted lexicon is counted once rather than twice.
          filter: LEXICON_DELETED_FILTER,
        },
        operationName: operationNameOf(COUNT_DELETED_LEXICONS),
      });
      // Records, not `count`, and RAW records: a row this app could not parse
      // is still a row in the trash, and the link would be wrong to skip it.
      const records = response?.structuredDataObjects?.records ?? [];
      return records.length > DELETED_COUNT_MAX
        ? { count: DELETED_COUNT_MAX, exact: false }
        : { count: records.length, exact: true };
    }
  );
}

/**
 * True when `name` is already used by a different LIVE lexicon (BR-2).
 *
 * ONE EXACT-MATCH QUERY, on `nameLower` (review nit N14). BR-2 folds case, the
 * platform's filter does not, so the folding happens at write time and this
 * asks the filter a question it can answer. It replaced a scan of the entire
 * org — up to 100 sequential queries on every create, every rename and every
 * draft save, before a single byte was written.
 *
 * Deleted lexicons do not hold their names and never reach it: the filter is
 * the LIVE one. Soft delete frees the name immediately, which is why restoring
 * is the operation that can be blocked rather than deleting.
 *
 * WHAT IT STILL IS NOT is a uniqueness CONSTRAINT. An SDO registry has none to
 * offer and a JSON schema cannot express one, so two creates racing on the same
 * name can both miss and both write. Narrowing that from "one client-side scan
 * wide" to "one round trip wide" is the whole of what this change buys;
 * closing it needs a server-side lexicon service. The previous version of this
 * comment claimed "the platform is the real uniqueness gate", which was simply
 * untrue and is the kind of claim that stops someone building the real one
 * (review R3).
 *
 * A record written without `nameLower` — by a script, or by a client older than
 * this field — is invisible to the probe and can be duplicated. Every write
 * path here derives it in `toSummaryData`, which is why it is not a required
 * schema key: making it required would force the DETAIL records to carry a
 * meaningless one too, the way `isDeleted` already has to.
 */
export function isLexiconNameTaken(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  name: string,
  excludeLexiconId?: string
): Promise<boolean> {
  const nameLower = lexiconNameKey(name);
  if (!nameLower) {
    // No request at all: an empty name is refused by the form's own validation,
    // and asking the registry about it would be a round trip for a question
    // with one possible answer.
    return Promise.resolve(false);
  }
  return withLexiconSchemaId(
    exec,
    config.lexiconRegistryId,
    async (schemaId) => {
      const response = await exec<FetchLexiconsResponse>({
        query: FETCH_LEXICONS_BY_NAME_KEY,
        variables: { schemaId, filter: lexiconNameKeyFilter(nameLower) },
        operationName: operationNameOf(FETCH_LEXICONS_BY_NAME_KEY),
      });
      const records = response?.structuredDataObjects?.records ?? [];
      // By ID, not by name: a rename has to find its own record and ignore it,
      // and re-reading the name here would only re-implement the fold that the
      // filter has already applied.
      return records.some((record) => record.id !== excludeLexiconId);
    }
  );
}

/**
 * Hard cap on pages read in one full scan (`fetchAllLiveLexicons`, now the
 * only scan in this module — the deleted count is one bounded query).
 *
 * 100 pages x 100 records = 10,000 lexicons, twenty times the BR-16 entry
 * ceiling's order of magnitude for lexicon COUNT. A backstop against a server
 * that never returns a short page; it also bounds an org holding more than
 * 10,000 records of one kind, which then reads short rather than hanging.
 */
export const MAX_SCAN_PAGES = 100;

export interface CreateLexiconInput {
  readonly name: string;
  readonly actor: string;
  readonly now: string;
  /** Entries composed before the first save; each gets a real id here (BR-8). */
  readonly entries?: readonly LexiconEntryInput[];
}

/**
 * Creates a lexicon — with its initial entries when the dialog composed some
 * before Create — as ONE write; `revision` starts at 1 (BR-3).
 */
export function createLexicon(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  input: CreateLexiconInput
): Promise<Lexicon> {
  return withLexiconSchemaId(
    exec,
    config.lexiconRegistryId,
    async (schemaId) => {
      const id = uuid();
      const entriesId = uuid();
      const entries: LexiconEntry[] = (input.entries ?? []).map((entry) => ({
        entryId: uuid(),
        ...entry,
      }));
      // Every entry is new here, so all of them are screened.
      await assertNewRegexEntriesScreened([], entries, config.probeRegex);

      // DETAIL FIRST, THEN SUMMARY. A failure between the two leaves an orphan
      // detail — referenced by nothing, listed by nothing, invisible. The other
      // order would leave a summary in the list pointing at entries that were
      // never written, which is a lexicon that opens to an error.
      const entriesData: LexiconEntriesData = {
        recordPart: 'entries',
        // Inert on a detail, required by the schema on every record — see
        // `LexiconEntriesData`.
        isDeleted: false,
        lexiconId: id,
        revision: 1,
        entries,
      };
      const entriesResponse = await exec<CreateLexiconResponse>({
        query: CREATE_LEXICON_ENTRIES,
        variables: { id: entriesId, schemaId, data: entriesData },
        operationName: operationNameOf(CREATE_LEXICON_ENTRIES),
      });
      if (!entriesResponse?.createStructuredData) {
        throw new Error('Lexicon entries were not created.');
      }

      const summary: LexiconSummary = {
        id,
        name: input.name.trim(),
        revision: 1,
        entriesId,
        entryCount: liveEntries(entries).length,
        deletedEntryCount: deletedEntries(entries).length,
        // Stamped from the first write, like `recordPart`, and for the same
        // reason: both are filtered on, and a record missing either appears in
        // no list at all.
        isDeleted: false,
        createdDateTime: input.now,
        modifiedDateTime: input.now,
        createdBy: input.actor,
        modifiedBy: input.actor,
      };
      // Through `toSummaryData`, not assembled here: a create that built its
      // own `data` literal would be the one write path that could forget a
      // derived field like `nameLower`, and it is the path that decides whether
      // the name is unique in the first place.
      const response = await exec<CreateLexiconResponse>({
        query: CREATE_LEXICON,
        variables: { id, schemaId, data: toSummaryData(summary) },
        operationName: operationNameOf(CREATE_LEXICON),
      });
      const created = response?.createStructuredData;
      if (!created) {
        throw new Error('Lexicon was not created.');
      }
      const { entryCount: _count, ...rest } = summary;
      return { ...rest, id: created.id, entries };
    }
  );
}

/**
 * Screens the regex entries this write ADDS OR CHANGES (NFR-S8, review-gate M2).
 *
 * The module's write boundary, not the settings page: `validateRegexEntry` was
 * reachable only from the drawer and the CSV importer, while
 * `lexiconMatchWorker` documents its input as "already screened" — an
 * invariant nothing enforced. Anything written by another client, a migration
 * or a script arrived unscreened with only the 30 s terminate as a backstop.
 *
 * THAT IS STILL TRUE OF THE MEASUREMENT, and the sentence below used to
 * overstate it. This gate covers every write THROUGH THIS MODULE; it does not
 * cover a pattern written straight to the registry, because nothing client-side
 * can. `asEntry` now applies the SYNTACTIC screen on the way back in, so such a
 * pattern cannot reach the matcher invalid or out of shape — but its measured
 * cost is unverified, and the match worker's own budgets are what bound it.
 *
 * Deliberately scoped to what changed. Screening the whole entry set would
 * mean a lexicon holding a regex written before a rule existed could no longer
 * be renamed or appended to — the author would be locked out of their own data
 * by a rule they cannot see. An untouched entry is left alone; only what this
 * write introduces has to pass.
 *
 * IT NOW MEASURES, NOT ONLY SCREENS (ADR-001 as amended by review gate #3).
 * `validateRegexEntry` covers only what is certain from syntax; the cost of a
 * pattern is decided by `probeRegexPerformance`, which times it at page scale
 * in a disposable worker. Three inference-based screens were bypassed before
 * this, the last accepting `(\w)*` x10 at 133,679 ms on a 40-character input,
 * so this boundary is where the measurement is made authoritative for EVERY
 * write path — the drawer, the CSV importer, a migration or a script.
 *
 * FAIL CLOSED. A probe that cannot run refuses the write (`P2`), because the
 * alternative is a save that silently skipped the only real gate.
 *
 * COST, STATED HONESTLY. The probe is per changed regex entry and runs
 * serially, so an ordinary edit (one or two entries) is imperceptible while a
 * CSV import introducing many REGEX entries pays roughly the probe's own time
 * per entry — tens of milliseconds each for a benign pattern. That is the
 * correct trade for a safety gate on a bulk operation that is already slow;
 * literal-term entries are unaffected, and unchanged entries are not re-probed.
 */
async function assertNewRegexEntriesScreened(
  before: readonly LexiconEntry[],
  after: readonly LexiconEntry[],
  probe: typeof probeRegexPerformance = probeRegexPerformance
): Promise<void> {
  const priorById = new Map(before.map((e) => [e.entryId, e]));
  for (const entry of after) {
    if (entry.type !== 'regex') {
      continue;
    }
    const prior = priorById.get(entry.entryId);
    const unchanged =
      prior && prior.term === entry.term && prior.type === entry.type;
    if (unchanged) {
      continue;
    }
    if (entry.term.length > ENTRY_REGEX_MAX_LENGTH) {
      throw new Error(
        `A pattern cannot be longer than ${ENTRY_REGEX_MAX_LENGTH} characters.`
      );
    }
    const screened = validateRegexEntry(entry.term);
    if (!screened.ok) {
      // The screen's own message: it names the rule and is written for the
      // person who wrote the pattern (U5-FR7). Never echoes the pattern.
      throw new Error(screened.message);
    }
    // The measured gate. `failRegexScreen` is reused so a slow pattern is
    // reported through the same table as every other rejection.
    const probed = await probe(entry.term);
    if (!probed.ok) {
      throw new Error(
        failRegexScreen(
          probed.failure === 'too-slow'
            ? 'P1_MEASURED_TOO_SLOW'
            : 'P2_PROBE_UNAVAILABLE'
        ).message
      );
    }
  }
}

/**
 * Applies `mutate` to a lexicon under the revision guard.
 *
 * Re-reads immediately before writing and compares `revision` with what the
 * caller based its edit on. A mismatch ABORTS rather than merging blindly, so
 * an edit made against a stale read is refused instead of overwriting the other
 * actor's work.
 *
 * RESIDUAL RISK, and it is not small: this is a check-then-write, not a
 * compare-and-swap. `updateStructuredData` accepts no expected-revision
 * argument (verified against the platform schema — NFR-U5-9), so nothing
 * closes the window between the read above and the write below. Two editors of
 * one shared lexicon who overlap inside that window still lose an edit set,
 * and both audit `outcome` events record `committed` with the same
 * `revisionTo`. The guard narrows the window; it cannot eliminate it, and this
 * comment used to claim otherwise (review-gate B8). Removing the risk needs
 * either a CAS argument on the platform mutation or a server-side lexicon
 * service.
 *
 * Returns the new revision alongside the lexicon so the audit `outcome` event
 * can record `revisionTo` (ADR-003).
 */
export async function updateLexiconGuarded(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  args: {
    readonly lexiconId: string;
    readonly baseRevision: number;
    readonly actor: string;
    readonly now: string;
    readonly mutate: (current: Lexicon) => LexiconData;
  }
): Promise<{ readonly lexicon: Lexicon; readonly revisionTo: number }> {
  const current = await fetchLexiconById(exec, config, args.lexiconId);
  if (current.revision !== args.baseRevision) {
    throw new LexiconRevisionConflictError(
      args.lexiconId,
      args.baseRevision,
      current.revision,
      current.modifiedBy,
      current.modifiedDateTime
    );
  }

  // FROZEN BEFORE THE MUTATOR SEES IT (review nit N8).
  //
  // `entriesChanged` below is a reference comparison, and a reference
  // comparison can only answer this question if the old array still exists to
  // compare against. A mutator that edited entries IN PLACE would destroy the
  // before-state and hand back the same reference — so the summary write would
  // land, moving the revision, the entries write would be skipped, and the
  // change would be lost with nothing reporting it and the next read looking
  // consistent. No comparison can detect that after the fact; the original is
  // gone.
  //
  // So the array and its entries are frozen. An in-place edit now throws where
  // it happens instead of being silently dropped two writes later, which is
  // this function's whole stated preference — losing a change loudly beats
  // losing it quietly. O(n) on a list already in memory, against a network
  // round trip; the entries are declared `readonly` anyway, and this is what
  // makes that declaration true at runtime.
  const frozen: Lexicon = {
    ...current,
    entries: Object.freeze(current.entries.map((e) => Object.freeze(e))),
  };
  const mutated = args.mutate(frozen);
  await assertNewRegexEntriesScreened(
    frozen.entries,
    mutated.entries,
    config.probeRegex
  );
  if (mutated.entries.length > LEXICON_MAX_ENTRIES) {
    // Client-side mirror of the schema's `maxItems` (NFR-S8). The platform is
    // the real gate; failing here first turns an opaque platform rejection into
    // an actionable message.
    throw new Error(
      `A lexicon cannot hold more than ${LEXICON_MAX_ENTRIES} entries.`
    );
  }

  const revisionTo = current.revision + 1;
  const data: LexiconData = {
    ...mutated,
    revision: revisionTo,
    createdDateTime: current.createdDateTime,
    createdBy: current.createdBy,
    modifiedDateTime: args.now,
    modifiedBy: args.actor,
  };
  const entriesChanged = mutated.entries !== frozen.entries;

  return withLexiconSchemaId(
    exec,
    config.lexiconRegistryId,
    async (schemaId) => {
      // SUMMARY FIRST, AND THAT ORDER IS THE SAFETY PROPERTY.
      //
      // The revision lives on the summary and guards everything, exactly as it
      // did when a lexicon was one record — so nothing above this function has
      // to learn that storage was split. But two writes cannot be atomic here,
      // so one of them can land alone, and the two orders fail very
      // differently:
      //
      //   summary first — the revision moves, the entries do not. The change is
      //     LOST and reported as a failure. A retry reads the moved revision,
      //     conflicts, and shows the banner. Nothing is corrupted.
      //   entries first — the entries move, the revision does not. A retry
      //     passes the guard and re-applies the same mutation to entries that
      //     already have it, which for an append means writing them twice.
      //
      // Losing a change loudly beats applying it twice quietly, so: summary
      // first. The `entryCount` written here describes the INTENDED state, and
      // is corrected on the next read if the detail write never lands.
      const summaryResponse = await exec<UpdateLexiconResponse>({
        query: UPDATE_LEXICON,
        variables: {
          id: args.lexiconId,
          schemaId,
          data: toSummaryData({
            ...summaryOf({ id: args.lexiconId, ...data }),
            entryCount: liveEntries(data.entries).length,
            deletedEntryCount: deletedEntries(data.entries).length,
          }),
        },
        operationName: operationNameOf(UPDATE_LEXICON),
      });
      if (!summaryResponse?.updateStructuredData) {
        throw new Error('Lexicon was not updated.');
      }

      // A rename does not touch the entries, so it does not write them — which
      // is the other half of the payoff: renaming a 5,000-entry lexicon stops
      // rewriting 5,000 entries.
      if (entriesChanged) {
        const entriesResponse = await exec<UpdateLexiconResponse>({
          query: UPDATE_LEXICON_ENTRIES,
          variables: {
            id: current.entriesId,
            schemaId,
            data: toEntriesData({
              id: current.entriesId,
              lexiconId: args.lexiconId,
              revision: revisionTo,
              entries: data.entries,
            }),
          },
          operationName: operationNameOf(UPDATE_LEXICON_ENTRIES),
        });
        if (!entriesResponse?.updateStructuredData) {
          throw new Error('Lexicon entries were not updated.');
        }
      }

      return {
        lexicon: { id: args.lexiconId, ...data },
        revisionTo,
      };
    }
  );
}

/**
 * Writes a summary record as-is.
 *
 * Used to heal a stale `entryCount` and to flip the library flags. Both are
 * writes to the summary ALONE — neither reads or touches the detail record,
 * which is the point: a settings-list checkbox that had to load 5,000 entries
 * to change a boolean would give back exactly what the storage split bought.
 */
export async function writeSummary(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  summary: LexiconSummary
): Promise<void> {
  await withLexiconSchemaId(
    exec,
    config.lexiconRegistryId,
    async (schemaId) => {
      const response = await exec<UpdateLexiconResponse>({
        query: UPDATE_LEXICON,
        variables: {
          id: summary.id,
          schemaId,
          data: toSummaryData(summary),
        },
        operationName: operationNameOf(UPDATE_LEXICON),
      });
      // Checked, like every other write in this module (review R2). Returning
      // void regardless meant a rejected write — schema validation, a
      // permission, an outage — reported success: the toggle stayed on
      // optimistically and flipped back on the next fetch, with nothing shown
      // and nothing logged. The one write path that swallowed its own failure
      // was the one whose callers show no progress indicator.
      if (!response?.updateStructuredData) {
        throw new Error('Lexicon was not updated.');
      }
    }
  );
}

/**
 * Flips a lexicon's library flags — `enabled`, `autoApply`, `isDeleted` — and
 * nothing else.
 *
 * SUMMARY ONLY, AND THE REVISION IS DELIBERATELY NOT BUMPED. Both halves of
 * that are decisions, not shortcuts:
 *
 * The summary is the whole record these flags live on, so there is nothing to
 * read from the detail and nothing to write back to it. A toggle costs one read
 * and one write regardless of how large the lexicon is.
 *
 * `revision` guards a lexicon's CONTENT — its name and its entries (BR-6) — and
 * a flag is neither. Bumping it would mean that ticking a checkbox in the list
 * made a conflict banner appear in a drawer someone else had open, about a
 * change that could not possibly conflict with what they were editing. Leaving
 * it alone costs nothing in the other direction, because a drawer save re-reads
 * the record inside `updateLexiconGuarded` and carries the current flags
 * forward — so a save cannot clobber a toggle that happened while it was open.
 *
 * RESIDUAL RISK, and it is the same shape as `updateLexiconGuarded`'s: this is
 * a read-then-write with nothing closing the gap between the two, because
 * `updateStructuredData` takes no expected-revision argument and replaces the
 * record's `data` wholesale — there is no way to write one field. A content
 * save that lands inside that window is overwritten by the flag write, which
 * carries the name and revision it read a moment earlier. The rename is lost,
 * and the summary briefly disagrees with its own detail record.
 *
 * What it does NOT do is disarm BR-6. The revision going backwards makes the
 * next guarded write MISMATCH, so it fails closed with a conflict banner
 * rather than silently overwriting; the `entryCount` disagreement heals on the
 * next read. The damage is a lost rename and a conflict nobody can explain.
 *
 * The window is one round trip and both calls resolve the schema id through
 * one cached lookup below, so there is no resolution hop in the middle of it.
 * Closing it entirely needs a compare-and-swap on the platform mutation or a
 * server-side lexicon service — the same remediation `updateLexiconGuarded`
 * names, and recorded in `plans/lexicons-follow-ups.md`.
 *
 * Returns the written summary so the caller can store what it actually wrote.
 */
export function setLexiconFlags(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  args: {
    readonly lexiconId: string;
    readonly flags: {
      readonly enabled?: boolean;
      readonly autoApply?: boolean;
      /**
       * The SOFT delete (BR-5, as amended). It is here rather than in a delete
       * service of its own because it is the same write: one field on the
       * summary, no entries read, no revision moved. `purgeLexicon` is the
       * irreversible one, and it is a different function for that reason.
       */
      readonly isDeleted?: boolean;
    };
    readonly actor: string;
    readonly now: string;
  }
): Promise<LexiconSummary> {
  return withLexiconSchemaId(
    exec,
    config.lexiconRegistryId,
    async (schemaId) => {
      // Read as late as possible and write immediately: the gap between these
      // two lines is the whole exposure. Both are inside ONE schema resolution
      // so nothing can be interleaved by this function itself.
      const record = await fetchRecordById(exec, schemaId, args.lexiconId);
      const current = record ? lexiconSummaryFromSdo(record) : null;
      if (!current) {
        throw new LexiconNotFoundError(args.lexiconId);
      }
      const written: LexiconSummary = {
        ...current,
        ...args.flags,
        modifiedDateTime: args.now,
        modifiedBy: args.actor,
      };
      const response = await exec<UpdateLexiconResponse>({
        query: UPDATE_LEXICON,
        variables: {
          id: written.id,
          schemaId,
          data: toSummaryData(written),
        },
        operationName: operationNameOf(UPDATE_LEXICON),
      });
      if (!response?.updateStructuredData) {
        throw new Error('Lexicon was not updated.');
      }
      return written;
    }
  );
}

/** Appends entries, assigning each a stable `entryId` (BR-8, BR-26). */
export function appendEntries(
  current: Lexicon,
  inputs: readonly LexiconEntryInput[],
  makeId: () => string = uuid
): LexiconData {
  const added: LexiconEntry[] = inputs.map((input) => ({
    entryId: makeId(),
    ...input,
  }));
  return { ...toData(current), entries: [...current.entries, ...added] };
}

/**
 * Soft-deletes entries: tombstoned, not spliced out.
 *
 * The delete control is an unguarded icon on every row, so this is the
 * recoverable half of the pair. `purgeEntries` is the irreversible one, and it
 * is reached only through a confirmation.
 */
export function removeEntries(
  current: Lexicon,
  entryIds: readonly string[]
): LexiconData {
  const removing = new Set(entryIds);
  return {
    ...toData(current),
    entries: current.entries.map((e) =>
      removing.has(e.entryId) ? { ...e, isDeleted: true } : e
    ),
  };
}

/**
 * Drops entries from the array for good.
 *
 * The only thing that reclaims space under the schema's `maxItems: 5000`, which
 * counts tombstones like any other item — so a lexicon curated over years needs
 * this eventually or its writes start failing platform-side.
 */
export function purgeEntries(
  current: Lexicon,
  entryIds: readonly string[]
): LexiconData {
  const purging = new Set(entryIds);
  return {
    ...toData(current),
    entries: current.entries.filter((e) => !purging.has(e.entryId)),
  };
}

/**
 * Replaces entries by id, keeping each one's POSITION in the list.
 *
 * Cheap only because of a property the rest of this module pays for: every
 * write here is already a read-modify-write over the whole entries array, since
 * `updateStructuredData` replaces `data` wholesale and the platform offers no
 * patch. Updating one entry in place is therefore the same shape as removing
 * one, not a new kind of operation.
 *
 * An id with no match is ignored rather than appended. The caller's entry came
 * from a draft diffed against the open-time baseline, so a missing id means the
 * entry was deleted by someone else while the drawer sat open — and silently
 * resurrecting a colleague's deletion is the one outcome nobody wants. The
 * revision guard on the surrounding mutation is what usually catches that case
 * first; this is the backstop.
 */
export function updateEntries(
  current: Lexicon,
  entries: readonly LexiconEntry[]
): LexiconData {
  const byId = new Map(entries.map((e) => [e.entryId, e]));
  return {
    ...toData(current),
    entries: current.entries.map((e) => byId.get(e.entryId) ?? e),
  };
}

export function renameLexicon(current: Lexicon, name: string): LexiconData {
  return { ...toData(current), name: name.trim() };
}

/**
 * PERMANENTLY deletes a lexicon and its entries — the purge (BR-5, as amended).
 *
 * NOT what the table's Delete does any more. Delete raises `isDeleted` on the
 * summary and the row moves to the deleted view; this is what the deleted
 * view's trash icon runs, behind its own confirmation, and it is the only
 * irreversible operation in the feature. The asymmetry is the design: an
 * unguarded delete is survivable exactly because this one exists and is the
 * only way past it.
 *
 * Documents already redacted with the lexicon are unaffected either way:
 * applied detections carry a `LexiconEntrySnapshot` and never dereference a
 * live lexicon, which is what makes U5-FR2 structural rather than a policy
 * (see `models.ts`).
 */
export async function deleteLexicon(
  exec: GraphQLExecutor,
  config: LexiconServiceConfig,
  lexiconId: string
): Promise<void> {
  await withLexiconSchemaId(
    exec,
    config.lexiconRegistryId,
    async (schemaId) => {
      // Read the summary FIRST, only to learn `entriesId`. Once the summary is
      // gone there is nothing left that names the detail record, so a delete
      // that skips this step can never find its other half again.
      //
      // A summary that cannot be read is not a reason to refuse the delete:
      // the platform call below is the one that decides whether the lexicon is
      // gone, and a lexicon whose record is unparseable is exactly the one a
      // user most wants removed.
      let entriesId: string | undefined;
      try {
        const record = await fetchRecordById(exec, schemaId, lexiconId);
        entriesId = record
          ? (lexiconSummaryFromSdo(record)?.entriesId ?? undefined)
          : undefined;
      } catch {
        entriesId = undefined;
      }

      // SUMMARY FIRST, and that order is the safety property — the mirror of
      // the create. This order can leave an unreferenced detail, which nothing
      // lists and nothing renders. The reverse would leave a summary pointing
      // at entries that no longer exist: it appears in the list, and opening
      // it fails.
      const response = await exec<DeleteLexiconResponse>({
        query: DELETE_LEXICON,
        variables: { id: lexiconId, schemaId },
        operationName: operationNameOf(DELETE_LEXICON),
      });
      if (!response?.deleteStructuredData) {
        throw new Error('Lexicon was not deleted.');
      }

      if (entriesId) {
        // BEST EFFORT, deliberately. The lexicon IS deleted by the line above —
        // it is out of every list and every picker — so a failure here leaks an
        // invisible record rather than leaving the user's request half done.
        // Reporting it as a failed delete would be a lie about the thing they
        // asked for, and would invite a retry that then fails on the summary
        // that is already gone.
        try {
          await exec<DeleteLexiconResponse>({
            query: DELETE_LEXICON,
            variables: { id: entriesId, schemaId },
            operationName: operationNameOf(DELETE_LEXICON),
          });
        } catch {
          // See above.
        }
      }
      return undefined;
    }
  );
}
